import { useState, useEffect } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from './useAuth';

export interface PersistentRoom {
  id: string;
  owner_id: string;
  room_name: string;
  room_description?: string;
  room_icon: string;
  room_code: string;
  default_time_control: string;
  is_public: boolean;
  max_participants: number;
  room_tags: string[];
  total_games: number;
  created_at: string;
  updated_at: string;
  last_activity_at?: string;
}

export interface RoomInvitation {
  id: string;
  room_id: string | null;
  inviter_id: string;
  invitee_id: string;
  status: 'pending' | 'accepted' | 'declined' | 'expired';
  message?: string;
  expires_at: string;
  created_at: string;
  game_session_id?: string | null;
  challenge_type?: string;
  time_control?: string;
  challenger_color?: 'white' | 'black';
}

export interface Friendship {
  id: string;
  requester_id: string;
  addressee_id: string;
  status: 'pending' | 'accepted' | 'blocked';
  created_at: string;
}

export interface UserPresence {
  user_id: string;
  status: 'online' | 'offline' | 'playing' | 'away';
  last_seen_at: string;
  current_room_id?: string;
}

export const usePrivateRooms = () => {
  const { user } = useAuth();
  const [myRooms, setMyRooms] = useState<PersistentRoom[]>([]);
  const [publicRooms, setPublicRooms] = useState<PersistentRoom[]>([]);
  const [friends, setFriends] = useState<Friendship[]>([]);
  const [friendsPresence, setFriendsPresence] = useState<UserPresence[]>([]);
  const [invitations, setInvitations] = useState<RoomInvitation[]>([]);
  const [loading, setLoading] = useState(true);

  // Fetch user's own rooms
  const fetchMyRooms = async () => {
    if (!user) return;
    
    const { data, error } = await supabase
      .from('persistent_rooms')
      .select('*')
      .eq('owner_id', user.id)
      .order('updated_at', { ascending: false });
    
    if (error) {
      console.error('Error fetching my rooms:', error);
    } else {
      setMyRooms(data || []);
    }
  };

  // Fetch public rooms
  const fetchPublicRooms = async () => {
    const { data, error } = await supabase
      .from('persistent_rooms')
      .select('*')
      .eq('is_public', true)
      .order('last_activity_at', { ascending: false })
      .limit(20);
    
    if (error) {
      console.error('Error fetching public rooms:', error);
    } else {
      setPublicRooms(data || []);
    }
  };

  // Fetch user's friendships
  const fetchFriendships = async () => {
    if (!user) return;
    
    const { data, error } = await supabase
      .from('friendships')
      .select('*')
      .or(`requester_id.eq.${user.id},addressee_id.eq.${user.id}`)
      .order('created_at', { ascending: false });
    
    if (error) {
      console.error('Error fetching friendships:', error);
    } else {
      setFriends((data || []) as Friendship[]);
    }
  };

  // Fetch invitations
  const fetchInvitations = async () => {
    if (!user) return;
    
    const { data, error } = await supabase
      .from('room_invitations')
      .select('*')
      .or(`inviter_id.eq.${user.id},invitee_id.eq.${user.id}`)
      .order('created_at', { ascending: false });
    
    if (error) {
      console.error('Error fetching invitations:', error);
    } else {
      setInvitations((data || []) as RoomInvitation[]);
    }
  };

  // Fetch friends' presence
  const fetchFriendsPresence = async () => {
    if (!user) return;
    
    // Get accepted friends list
    const acceptedFriends = friends.filter(f => f.status === 'accepted');
    const friendIds = acceptedFriends.map(f => 
      f.requester_id === user.id ? f.addressee_id : f.requester_id
    );
    
    if (friendIds.length === 0) {
      setFriendsPresence([]);
      return;
    }
    
    const { data, error } = await supabase
      .from('user_presence')
      .select('*')
      .in('user_id', friendIds);
    
    if (error) {
      console.error('Error fetching friends presence:', error);
    } else {
      setFriendsPresence((data || []) as UserPresence[]);
    }
  };

  // Create a new persistent room
  const createPersistentRoom = async (roomData: {
    room_name: string;
    room_description?: string;
    room_icon?: string;
    default_time_control?: string;
    is_public?: boolean;
    max_participants?: number;
    room_tags?: string[];
  }) => {
    if (!user) throw new Error('User not authenticated');

    const room_code = `ROOM-${Math.random().toString(36).substring(2, 8).toUpperCase()}`;
    
    const { data, error } = await supabase
      .from('persistent_rooms')
      .insert({
        owner_id: user.id,
        room_code,
        room_name: roomData.room_name,
        room_description: roomData.room_description,
        room_icon: roomData.room_icon || '🏠',
        default_time_control: roomData.default_time_control || '5+0',
        is_public: roomData.is_public ?? true,
        max_participants: roomData.max_participants || 2,
        room_tags: roomData.room_tags || [],
      })
      .select()
      .single();

    if (error) {
      console.error('Error creating room:', error);
      throw error;
    }

    await fetchMyRooms();
    return data;
  };

  // Update room details
  const updateRoom = async (roomId: string, updates: Partial<PersistentRoom>) => {
    const { error } = await supabase
      .from('persistent_rooms')
      .update(updates)
      .eq('id', roomId);

    if (error) {
      console.error('Error updating room:', error);
      throw error;
    }

    await fetchMyRooms();
  };

  // Delete a room
  const deleteRoom = async (roomId: string) => {
    const { error } = await supabase
      .from('persistent_rooms')
      .delete()
      .eq('id', roomId);

    if (error) {
      console.error('Error deleting room:', error);
      throw error;
    }

    await fetchMyRooms();
  };

  // Send friend request by user ID
  const sendFriendRequest = async (addresseeId: string) => {
    if (!user) throw new Error('User not authenticated');

    // Check if friendship already exists
    const { data: existingFriendship, error: checkError } = await supabase
      .from('friendships')
      .select('*')
      .or(`and(requester_id.eq.${user.id},addressee_id.eq.${addresseeId}),and(requester_id.eq.${addresseeId},addressee_id.eq.${user.id})`)
      .single();

    if (checkError && checkError.code !== 'PGRST116') {
      console.error('Error checking existing friendship:', checkError);
      throw checkError;
    }

    if (existingFriendship) {
      throw new Error('Friendship already exists or request already sent');
    }

    const { error } = await supabase
      .from('friendships')
      .insert({
        requester_id: user.id,
        addressee_id: addresseeId,
        status: 'pending'
      });

    if (error) {
      console.error('Error sending friend request:', error);
      throw error;
    }

    await fetchFriendships();
  };

  // Accept/decline friend request
  const respondToFriendRequest = async (friendshipId: string, status: 'accepted' | 'blocked') => {
    const { error } = await supabase
      .from('friendships')
      .update({ status })
      .eq('id', friendshipId);

    if (error) {
      console.error('Error responding to friend request:', error);
      throw error;
    }

    await fetchFriendships();
  };

  // Send room invitation
  const sendRoomInvitation = async (roomId: string, inviteeId: string, message?: string) => {
    if (!user) throw new Error('User not authenticated');

    const { error } = await supabase
      .from('room_invitations')
      .insert({
        room_id: roomId,
        inviter_id: user.id,
        invitee_id: inviteeId,
        message,
        status: 'pending'
      });

    if (error) {
      console.error('Error sending invitation:', error);
      throw error;
    }

    await fetchInvitations();
  };

  // Quick challenge friend - creates room and invites immediately
  const quickChallengeFriend = async (friendId: string, timeControl: string = '5+0') => {
    if (!user) throw new Error('User not authenticated');

    try {
      // Create a new room using secure RPC function
      const roomCode = `ROOM-${Math.random().toString(36).substring(2, 8).toUpperCase()}`;
      
      const { data: room, error: roomError } = await supabase.rpc('create_temp_room', {
        _room_code: roomCode,
        _time_control: timeControl,
        _is_public: false,
        _max_participants: 2
      });

      if (roomError) {
        console.error('Error creating challenge room:', roomError);
        throw roomError;
      }

      if (!room || room.length === 0) {
        throw new Error('Failed to create challenge room');
      }

      // Send invitation to friend
      await sendRoomInvitation(room[0].id, friendId, `Quick challenge: ${timeControl}`);

      return room[0];
    } catch (error) {
      console.error('Error in quick challenge:', error);
      throw error;
    }
  };

  // Accept room invitation securely
  const acceptRoomInvitation = async (invitationId: string) => {
    if (!user) throw new Error('User not authenticated');

    try {
      console.log('Calling accept_room_invitation RPC for invitation:', invitationId);
      
      const { data: roomData, error } = await supabase.rpc('accept_room_invitation', {
        _invitation_id: invitationId
      });

      if (error) {
        console.error('Error accepting invitation:', error);
        
        // For already-accepted invitations, try to find the room and return it
        if (error.message?.includes('invitation_already_accepted') || 
            error.message?.includes('invitation_not_found')) {
          console.log('Invitation already processed, fetching room info from invitation record');
          
          // Try to get the invitation details first
          const { data: invData, error: invError } = await supabase
            .from('room_invitations')
            .select('room_id')
            .eq('id', invitationId)
            .single();
          
          if (!invError && invData) {
            // Try to find the room in persistent_rooms first
            const { data: persistentRoom } = await supabase
              .from('persistent_rooms')
              .select('room_code')
              .eq('id', invData.room_id)
              .single();
            
            if (persistentRoom?.room_code) {
              console.log('Found room code from persistent room:', persistentRoom.room_code);
              await fetchInvitations();
              return { room_id: invData.room_id, room_code: persistentRoom.room_code };
            }
            
            // If not in persistent rooms, check regular rooms
            const { data: regularRoom } = await supabase
              .from('rooms')
              .select('room_code')
              .eq('id', invData.room_id)
              .single();
            
            if (regularRoom?.room_code) {
              console.log('Found room code from regular room:', regularRoom.room_code);
              await fetchInvitations();
              return { room_id: invData.room_id, room_code: regularRoom.room_code };
            }
          }
        }
        
        // Handle specific error types from the database function
        if (error.message?.includes('authentication_required')) {
          throw new Error('AUTHENTICATION_REQUIRED');
        } else if (error.message?.includes('invitation_not_found')) {
          throw new Error('INVITATION_NOT_FOUND');
        } else if (error.message?.includes('invitation_already_accepted')) {
          throw new Error('INVITATION_ALREADY_ACCEPTED');
        } else if (error.message?.includes('room_not_found')) {
          throw new Error('ROOM_NOT_FOUND');
        } else if (error.message?.includes('room_full')) {
          throw new Error('ROOM_FULL');
        } else {
          throw error;
        }
      }

      console.log('Room invitation accepted successfully:', roomData);

      // Refresh invitations after acceptance
      await fetchInvitations();

      return roomData?.[0];
    } catch (error) {
      console.error('Error in accept room invitation:', error);
      throw error;
    }
  };

  // Decline room invitation
  const declineRoomInvitation = async (invitationId: string) => {
    if (!user) throw new Error('User not authenticated');

    const { error } = await supabase
      .from('room_invitations')
      .update({ status: 'declined' })
      .eq('id', invitationId)
      .eq('invitee_id', user.id);

    if (error) {
      console.error('Error declining invitation:', error);
      throw error;
    }

    await fetchInvitations();
  };

  // Update user presence
  const updatePresence = async (status: UserPresence['status'], currentRoomId?: string) => {
    if (!user) return;

    const { error } = await supabase
      .from('user_presence')
      .upsert(
        {
          user_id: user.id,
          status,
          current_room_id: currentRoomId,
          last_seen_at: new Date().toISOString()
        },
        {
          onConflict: 'user_id',
          ignoreDuplicates: false
        }
      );

    if (error) {
      console.error('Error updating presence:', error);
    }
  };

  // Initialize data and set up real-time subscriptions
  useEffect(() => {
    if (!user) return;

    const initializeData = async () => {
      setLoading(true);
      
      // Update presence to online first
      await updatePresence('online');
      
      // Fetch base data
      await Promise.all([
        fetchMyRooms(),
        fetchPublicRooms(),
        fetchInvitations(),
      ]);
      
      // Fetch friendships first, then presence
      await fetchFriendships();
      
      setLoading(false);
    };

    initializeData();

    // Set up real-time subscriptions
    const roomsChannel = supabase
      .channel('persistent_rooms_changes')
      .on('postgres_changes', { 
        event: '*', 
        schema: 'public', 
        table: 'persistent_rooms' 
      }, () => {
        fetchMyRooms();
        fetchPublicRooms();
      })
      .subscribe();

    // Set up presence real-time subscription
    const presenceChannel = supabase
      .channel('user_presence_changes')
      .on('postgres_changes', {
        event: '*',
        schema: 'public',
        table: 'user_presence'
      }, () => {
        fetchFriendsPresence();
      })
      .subscribe();

    // Update presence to offline when leaving
    const handleBeforeUnload = () => {
      updatePresence('offline');
    };

    window.addEventListener('beforeunload', handleBeforeUnload);

    return () => {
      roomsChannel.unsubscribe();
      presenceChannel.unsubscribe();
      window.removeEventListener('beforeunload', handleBeforeUnload);
      updatePresence('offline');
    };
  }, [user]);

  // Fetch friends presence whenever friends list changes
  useEffect(() => {
    if (!user || friends.length === 0) {
      setFriendsPresence([]);
      return;
    }
    
    fetchFriendsPresence();
  }, [friends, user]);

  return {
    myRooms,
    publicRooms,
    friends,
    friendsPresence,
    invitations,
    loading,
    createPersistentRoom,
    updateRoom,
    deleteRoom,
    sendFriendRequest,
    respondToFriendRequest,
    sendRoomInvitation,
    quickChallengeFriend,
    acceptRoomInvitation,
    declineRoomInvitation,
    updatePresence,
    refetch: async () => {
      await fetchMyRooms();
      await fetchPublicRooms();
      await fetchFriendships();
      await fetchInvitations();
      await fetchFriendsPresence();
    }
  };
};